import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Disc3, X, AlertTriangle } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const STATUS_STYLE = {
  unused: 'text-slate-300 bg-slate-700/40',
  partially_used: 'text-amber-300 bg-amber-500/20',
  exhausted: 'text-red-300 bg-red-500/20',
  void: 'text-red-400 bg-red-900/40',
};

export const RollUsageSection = () => {
  const [query, setQuery] = useState('');
  const [rolls, setRolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [adjust, setAdjust] = useState({ total_capacity: '', disabled: false, product_type: '', reason: '' });

  const fetchRolls = async (q = '') => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/rolls${q ? `?q=${encodeURIComponent(q)}` : ''}`);
      setRolls(res.data.rolls || []);
    } catch (e) {
      toast.error('Failed to load rolls');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRolls(); }, []);

  const openDetail = async (rid) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/rolls/${encodeURIComponent(rid)}`);
      setDetail(res.data);
      setAdjust({
        total_capacity: res.data.roll.total_capacity,
        disabled: res.data.roll.disabled,
        product_type: res.data.roll.product_type || '',
        reason: '',
      });
    } catch (e) {
      toast.error('Failed to load roll detail');
    }
  };

  const saveAdjust = async () => {
    if (!adjust.reason.trim()) { toast.error('A reason is required for manual roll changes.'); return; }
    try {
      await axios.put(`${API_BASE_URL}/api/admin/rolls/${encodeURIComponent(detail.roll.roll_source_id)}`, {
        reason: adjust.reason.trim(),
        total_capacity: parseInt(adjust.total_capacity, 10),
        disabled: adjust.disabled,
        product_type: adjust.product_type || null,
      });
      toast.success('Roll updated');
      openDetail(detail.roll.roll_source_id);
      fetchRolls(query);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update roll');
    }
  };

  const StatusBadge = ({ status }) => (
    <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STATUS_STYLE[status] || 'text-slate-300 bg-slate-700/40'}`}>
      {(status || '').replace('_', ' ')}
    </span>
  );

  return (
    <div className="space-y-6" data-testid="roll-usage-section">
      <div>
        <h2 className="text-3xl font-bold text-primary mb-1 flex items-center gap-2"><Disc3 className="w-6 h-6 text-amber-400" /> Roll Usage &amp; TC Roll Credits</h2>
        <p className="text-secondary text-sm">Search a roll to inspect its capacity, installation history and adjust total credits.</p>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); fetchRolls(query); }} className="flex gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <input className="tc-input tc-focus-ring pl-9" placeholder="Search Roll Number / Tube Serial…"
            data-testid="roll-search-input"
            value={query} onChange={(e) => setQuery(e.target.value.toUpperCase())} />
        </div>
        <button type="submit" className="tc-button-primary" data-testid="roll-search-btn">Search</button>
      </form>

      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="tc-spinner" /><span className="ml-4 text-primary">Loading…</span></div>
      ) : rolls.length === 0 ? (
        <div className="tc-card p-8 text-center text-secondary">No rolls found. Rolls appear here once dealers register installations.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rolls.map((r) => (
            <button key={r.roll_source_id} onClick={() => openDetail(r.roll_source_id)}
              data-testid={`roll-card-${r.roll_source_id}`}
              className="tc-card p-4 text-left hover:border-amber-500/60 transition-colors">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono font-bold text-tc-gold">{r.roll_number}</span>
                <StatusBadge status={r.status} />
              </div>
              <div className="grid grid-cols-4 gap-2 text-center text-xs">
                <div><p className="text-muted">Total</p><p className="text-primary font-bold">{r.total_capacity}</p></div>
                <div><p className="text-muted">Used</p><p className="text-amber-400 font-bold">{r.used_capacity}</p></div>
                <div><p className="text-muted">Reserved</p><p className="text-blue-400 font-bold">{r.reserved_capacity}</p></div>
                <div><p className="text-muted">Remaining</p><p className="text-green-400 font-bold">{r.remaining_capacity}</p></div>
              </div>
              <p className="text-muted text-xs mt-2">{r.total_certificates} certificate(s)</p>
            </button>
          ))}
        </div>
      )}

      {/* Roll detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setDetail(null)}>
          <div className="bg-slate-900 border border-amber-500/30 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="roll-detail-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 sticky top-0 bg-slate-900">
              <h3 className="text-xl font-bold text-tc-gold font-mono">{detail.roll.roll_number}</h3>
              <button onClick={() => setDetail(null)} className="text-muted hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6 space-y-6">
              {/* Capacity summary */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
                <div className="tc-card p-3"><p className="text-muted text-xs">Total</p><p className="text-primary text-xl font-bold">{detail.roll.total_capacity}</p></div>
                <div className="tc-card p-3"><p className="text-muted text-xs">Used</p><p className="text-amber-400 text-xl font-bold">{detail.roll.used_capacity}</p></div>
                <div className="tc-card p-3"><p className="text-muted text-xs">Reserved</p><p className="text-blue-400 text-xl font-bold">{detail.roll.reserved_capacity}</p></div>
                <div className="tc-card p-3"><p className="text-muted text-xs">Remaining</p><p className="text-green-400 text-xl font-bold">{detail.roll.remaining_capacity}</p></div>
                <div className="tc-card p-3"><p className="text-muted text-xs">Status</p><div className="mt-1"><StatusBadge status={detail.roll.status} /></div></div>
              </div>

              {/* Installation history */}
              <div>
                <h4 className="text-lg font-semibold text-primary mb-2">Installation History</h4>
                <div className="overflow-x-auto tc-card p-0">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-muted border-b border-slate-700">
                        <th className="text-left px-3 py-2">Certificate</th>
                        <th className="text-left px-3 py-2">Customer</th>
                        <th className="text-left px-3 py-2">Vehicle</th>
                        <th className="text-left px-3 py-2">Coverage</th>
                        <th className="text-left px-3 py-2">Credits</th>
                        <th className="text-left px-3 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.history.map((h) => (
                        <tr key={h.warranty_id} className="border-b border-slate-800" data-testid={`history-row-${h.warranty_id}`}>
                          <td className="px-3 py-2 font-mono text-tc-gold">{h.certificate_number}</td>
                          <td className="px-3 py-2 text-primary">{h.client_name}</td>
                          <td className="px-3 py-2 text-secondary">{h.vehicle}</td>
                          <td className="px-3 py-2 text-secondary">{h.coverage_summary}</td>
                          <td className="px-3 py-2 text-amber-400 font-mono">{h.capacity_consumed}</td>
                          <td className="px-3 py-2 capitalize text-secondary">{h.status}</td>
                        </tr>
                      ))}
                      {detail.history.length === 0 && (
                        <tr><td colSpan="6" className="px-3 py-6 text-center text-muted">No installations recorded.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Admin adjustment */}
              <div className="tc-card p-4 space-y-3 border border-amber-500/30">
                <h4 className="text-sm font-semibold text-amber-400 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Administrative Adjustment</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="tc-label text-xs">Total Capacity</label>
                    <input type="number" min="0" className="tc-input tc-focus-ring" data-testid="roll-adjust-capacity"
                      value={adjust.total_capacity} onChange={(e) => setAdjust({ ...adjust, total_capacity: e.target.value })} />
                  </div>
                  <div>
                    <label className="tc-label text-xs">Product / Film Type</label>
                    <input className="tc-input tc-focus-ring" placeholder="e.g. Exterior PPF"
                      value={adjust.product_type} onChange={(e) => setAdjust({ ...adjust, product_type: e.target.value })} />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm cursor-pointer text-secondary">
                      <input type="checkbox" checked={adjust.disabled} data-testid="roll-adjust-disabled"
                        onChange={(e) => setAdjust({ ...adjust, disabled: e.target.checked })} />
                      Disable this roll
                    </label>
                  </div>
                </div>
                <div>
                  <label className="tc-label text-xs">Reason (required)</label>
                  <input className="tc-input tc-focus-ring" placeholder="Reason for this change…" data-testid="roll-adjust-reason"
                    value={adjust.reason} onChange={(e) => setAdjust({ ...adjust, reason: e.target.value })} />
                </div>
                <button onClick={saveAdjust} className="tc-button-primary" data-testid="roll-adjust-save">Save Adjustment</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RollUsageSection;
